<script setup>
  import { ref, computed, onMounted } from 'vue';
  import { useSpotStore } from '@/stores/SpotStore';
  import { useUserStore } from '@/stores/UserStore';
  import { useGoogleAnalyticsStore } from '@/stores/GoogleAnalyticsStore';
  import BaseModal from '@/components/base/BaseModal.vue';
  import Checkbox from '@/components/controls/Checkbox.vue';

  const props = defineProps({
    profiles: {
      type: Array,
      required: true
    }
  });

  const spotStore = useSpotStore();
  const userStore = useUserStore();
  const googleAnalyticsStore = useGoogleAnalyticsStore();

  const showModal = defineModel('showProfileModal', { default: true });

  const refreshProfilesTimeout = ref(null);

  const saveToProfileHeader = computed(() => `Save ${spotStore.spotName} to Profile.`);
  const saveToProfileInstructions = computed(() => `Choose the profile${props.profiles.length > 1 ? 's' : ''} you would like to save <strong>${spotStore.spotName}</strong> to.`);

  const settings = computed(() => ({
    name: spotStore.spotName,
    lat: spotStore.lat,
    lon: spotStore.lon
  }));

  const favoriteId = computed(() => {
    return (profileId) => {
      const favoritedSpot = spotStore.isSpotFavoritedInProfile(profileId);
      return favoritedSpot ? favoritedSpot[1] : null;
    }
  });

  const trackSpotAdd = () => {
    googleAnalyticsStore.sendEvent('Favorite_Spot_Add', {
      Type: spotStore.spotId ? 'Station' : 'Arbitrary',
    });
  };

  async function handleContainerProfileSelection(event) {
    const profileId = event.target.value;

    if (event.target.checked) {
      const data = await userStore.addSpotToProfile(profileId, spotStore.spotId, settings.value);

      trackSpotAdd();

      if (data.status_code === 0) {
        event.target.setAttribute('data-favorite-id', data.status_message);
      }

      refreshProfiles()
    } else {
      const favoriteId = event.target.dataset.favoriteId;
      await userStore.removeSpotFromProfile(profileId, favoriteId);

      event.target.removeAttribute('data-favorite-id');
      refreshProfiles();
    }

  }

  const refreshProfiles = (delay = 250) => {
    if (refreshProfilesTimeout.value) {
      clearTimeout(refreshProfilesTimeout.value);
    }

    refreshProfilesTimeout.value = setTimeout(() => {
      userStore.getProfiles(true);
      clearTimeout(refreshProfilesTimeout.value);

      refreshProfilesTimeout.value = null;
    }, delay);
  }

  onMounted(async () => {
    if (props.profiles.length === 1 && !favoriteId.value(props.profiles[0].profile_id)) {
      await userStore.addSpotToProfile(props.profiles[0].profile_id, spotStore.spotId, settings.value);
      trackSpotAdd();
      refreshProfiles(0);
    }
  })

</script>

<template>
  <BaseModal
    v-model="showModal"
    :allowOutsideClick="false"
  >
    <header class="jwx-modal__header">
      <h2 class="jwx-modal__title">{{ saveToProfileHeader }}</h2>
    </header>          
    <div class="jwx-modal__body">
      <div class="jwx-modal__section">
        <p v-html="saveToProfileInstructions" class="jwx-modal__text"></p>
      </div>
      <div class="jwx-modal__section">
        <ul class="jwx-save-to-selector">
          <li v-for="profile in profiles" class="jwx-save-to-selector__item" :key="profile.profile_id">
            <Checkbox
              @change="handleContainerProfileSelection"
              :name="`jwx-${profile.profile_id}`"
              :id="`jwx-${profile.profile_id}`"
              :value="profile.profile_id"
              :checked="spotStore.isSpotFavoritedInProfile(profile.profile_id)"
              :data-favorite-id="favoriteId(profile.profile_id)"
            >
              <h4 class="jwx-save-to-selector__label-title">
                {{ profile.name }}
              </h4>
            </Checkbox>
          </li>
        </ul>
      </div>
    </div>
    <div class="jwx-modal__footer">
      <div class="jwx-modal__actions">
        <button 
          type="button" 
          @click.prevent="showModal = false;"
          class="jwx-button jwx-button--success"
        >
          Done
        </button>
      </div>
    </div>                  
  </BaseModal>  
</template>